<template>
  <rhcn-drawer
    :title="titleText"
    @on-close="onClose">
    <div v-loading="dataLoading" class="contact-form">
      <validation-component
        ref="contactFormRef"
        :validation-condition="contactCondition">
      </validation-component>
      <div v-if="fromTransfer">
        <el-checkbox v-model="isSaveContact">
          <span class="btn-save-contact">{{$t('message.contact.saveContact')}}</span>
        </el-checkbox>
      </div>
      <div class="margin-top-18 margin-bottom-30 bottom-box">
        <el-button
          :loading="requesting"
          :disabled="dataLoading"
          style="width:100%;" type="primary"
          @click="onSubmit">{{fromTransfer ? $t('message.common.next') : $t('message.common.confirm')}}</el-button>
        <p v-if="fromTransfer" class="btn-previous margin-top-10" @click="onClose">{{$t('message.common.previous')}}</p>
      </div>
    </div>
  </rhcn-drawer>
</template>
<script>
import { mapGetters } from 'vuex';

import RhcnDrawer from '@/components/drawer/index.vue';
import { ShowWarningMessage, ShowSuccessMessage } from '@/components/modal/tips/message-tips';
import { REQUIRED, PHONE_NUMBER } from '@/components/validation/tool/validator';
import ValidationComponent from '@/components/validation/validation.vue';
import GlobalData from '@/utils/global/index';
import { getEnantiomericValue, initSelectedData } from '@/utils/tools/constant';
import { arr2obj, shallowClones } from '@/utils/tools/utils';
import AccountInfo from '@/views/account/information/api/account-info';
import {
  getCountryGroupListByName, getPayeeInfo,
  postUpdatePayeeVerify, postAddPayeeVerify,
  uploadMaterial, getMaterialInfoList,
  uploadMaterialUseTemplate, postValidatePayee,
} from '@/views/transfer/api/contact';
import {
  ACCOUNT_BELONG_OPTIONS, ENTITY_TYPE_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
} from '@/views/transfer/api/options';

export default {
  components: { RhcnDrawer, ValidationComponent },
  props: {
    businessType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      titleText: '',
      dataLoading: false,
      contactId: '',
      contactCondition: [
        {
          key: 'accountBelong',
          name: 'message.contact.beneficiaryCategory',
          type: 'select',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          options: ACCOUNT_BELONG_OPTIONS,
          isInternationalization: true,
          changeValue: (value) => {
            if (value === '2') {
              this.$refs.contactFormRef.setValidationSelectOptions('payeeCountry', shallowClones(this.vatCountryList));
            } else {
              this.$refs.contactFormRef.setValidationSelectOptions('payeeCountry', shallowClones(this.countryList));
            }
            this.$refs.contactFormRef.setItemValue('payeeCountry', '');
            this.onChangeValue();
            this.getMaterialList();
          },
        },
        {
          key: 'entityType',
          name: 'message.contact.beneficiaryType',
          type: 'select',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          options: ENTITY_TYPE_OPTIONS,
          isInternationalization: true,
          changeValue: () => {
            this.$refs.contactFormRef.setItemValue('vatNumber', '');
            this.onChangeValue();
            this.getMaterialList();
          },
        },
        {
          key: 'payoutCurrency',
          name: 'message.contact.accountCurrency',
          type: 'select',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          options: [],
          option: {
            label: 'value',
            value: 'value',
          },
          changeValue: () => {
            this.onChangeValue();
            this.getMaterialList();
          },
        },
        {
          key: 'bankCountry',
          name: 'message.contact.bankCountryRegion',
          type: 'select',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          options: [],
          option: {
            label: 'description',
            value: 'value',
          },
          changeValue: () => {
            this.onChangeValue();
          },
        },
        // 银行名称，payout 商户显示
        {
          key: 'bankName1',
          name: 'message.contact.bankName',
          type: 'input',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          isHide: true,
          maxLength: 79,
          validator: this.validateBankName,
        },
        {
          key: 'vatNumber',
          name: 'CPF',
          type: 'input',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          maxLength: 11,
          isHide: true,
          validator: this.validateVatNumber,
          changeValue: (value) => {
            const v = value.replace(/[^0-9]/g, '');
            this.$refs.contactFormRef.setItemValue('vatNumber', v);
          },
        },
        {
          key: 'paymentMethod',
          name: 'message.contact.paymentMethod',
          type: 'select',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          options: PAYMENT_METHOD_OPTIONS,
          tipList: [{
            key: 'Local: ',
            value: this.$t('message.contact.localDescription'),
          }, {
            key: 'Swift: ',
            value: this.$t('message.contact.swiftDescription'),
          }],
          changeValue: () => {
            this.onChangeValue();
          },
        },
        {
          key: 'payee',
          name: 'message.contact.beneficiaryName',
          type: 'input',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
        },
        {
          key: 'companyName',
          name: 'CompanyName',
          type: 'input',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          isHide: true,
          maxLength: 29,
          changeValue: (value) => {
            const v = value.replace(/[^a-zA-Z]/g, '');
            this.$refs.contactFormRef.setItemValue('companyName', v);
          },
        },
        {
          key: 'firstName',
          name: 'FirstName',
          type: 'input',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          isHide: true,
          maxLength: 29,
          changeValue: (value) => {
            const v = value.replace(/[^a-zA-Z]/g, '');
            this.$refs.contactFormRef.setItemValue('firstName', v);
          },
        },
        {
          key: 'lastName',
          name: 'LastName',
          type: 'input',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          isHide: true,
          maxLength: 29,
          changeValue: (value) => {
            const v = value.replace(/[^a-zA-Z]/g, '');
            this.$refs.contactFormRef.setItemValue('lastName', v);
          },
        },
        {
          key: 'idNumber',
          name: 'message.contact.certificateNo',
          type: 'input',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          isHide: true,
        },
        {
          key: 'payeeCountry',
          name: 'message.contact.beneficiaryCountry',
          type: 'select',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          options: [],
          option: {
            label: 'description',
            value: 'value',
          },
        },
        {
          key: 'payeeAddress',
          name: 'message.contact.beneficiaryAddress',
          type: 'input',
          defValue: '',
          rule: [],
          isShowStar: true,
          validator: this.payeeAddressValidate,
        },
        {
          key: 'address',
          name: 'Address',
          type: 'input',
          defValue: '',
          rule: [],
          isShowStar: true,
          isHide: true,
          maxLength: 199,
          validator: this.validateAddress,
          changeValue: (value) => {
            const v = value.replace(/[^a-zA-Z]/g, '');
            this.$refs.contactFormRef.setItemValue('address', v);
          },
        },
        {
          key: 'phone',
          name: 'message.contact.mobilePhone',
          type: 'input',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          isHide: true,
          maxLength: 11,
          prepend: '',
          validator: this.validatePhone,
          changeValue: (value) => {
            const v = value.match(/^\d*(\d{0,10})/g)[0];
            this.$refs.contactFormRef.setItemValue('phone', v);
          },
        },
        {
          key: 'zipCode',
          name: 'ZipCode',
          type: 'input',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          isHide: true,
          maxLength: 7,
          validator: this.validateZipCode,
          changeValue: (value) => {
            const v = value.replace(/[^a-zA-Z0-9]/g, '');
            this.$refs.contactFormRef.setItemValue('zipCode', v ? v.toUpperCase() : v);
          },
        },
        {
          key: 'bankAccount',
          name: 'message.contact.accountName',
          type: 'input',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
        },
        {
          key: 'bankName',
          name: 'message.contact.bankName',
          type: 'select',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          isHide: true,
          options: [],
          filterMethod: (value) => {
            this.$refs.contactFormRef.setValidationSelectOptions('bankName', this.filterBank(value));
          },
        },
        {
          key: 'cardNumber',
          name: 'message.contact.accountNumber',
          type: 'input',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          changeValue: (value) => {
            const v = value.replace(/\s+/g, '');
            this.$refs.contactFormRef.setItemValue('cardNumber', v.toUpperCase());
          },
        },
        {
          key: 'bankAddress',
          name: 'message.tableCommon.bankAddress',
          type: 'input',
          defValue: '',
          rule: [],
          isShowStar: true,
          validator: this.bankAddressValidate,
        },
        {
          key: 'swiftCode',
          name: 'message.contact.swiftCode',
          type: 'input',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          isHide: true,
          changeValue: (value) => {
            const v = value || '';
            this.$refs.contactFormRef.setItemValue('swiftCode', v.toUpperCase());
          },
        },
        {
          key: 'sortCode',
          name: 'message.contact.sortCode',
          type: 'input',
          defValue: '',
          rule: [REQUIRED],
          isShowStar: true,
          isHide: true,
          changeValue: (value) => {
            this.$refs.contactFormRef.setItemValue('sortCode', value.toUpperCase());
          },
        },
        {
          key: 'material',
          name: 'message.contact.file',
          type: 'upload_file',
          defValue: [],
          rule: [REQUIRED],
          isShowStar: true,
          isHide: true,
          uploadRange: {
            fileSize: 50, // 50M
            uploadFileList: [],
            uploadTips: this.$t('message.upload.zipOrImgtip'),
            uploadFileTips: this.$t('message.contact.fileTips'),
            // fileTypeRule: 'image/png,image/jpeg,image/jpg,application/pdf,application/zip,application/x-zip-compressed',
            fileTypeRuleTips: this.$t('message.supportFileErrorTip', { type: '.png,.jpeg,.jpg,.pdf,.zip' }),
            hideErrorMessage: true,
            fileUploadFunc: async (file) => {
              const fileName = file.name;
              const fileNameArr = fileName.split('.');
              const ext = fileNameArr[fileNameArr.length - 1].toLowerCase();
              const extList = ['png', 'jpg', 'jpeg', 'pdf', 'zip'];
              if (extList.indexOf(ext) === -1) { // 不支持格式， 处理兼容性问题
                ShowWarningMessage(this.$t('message.supportFileErrorTip', { type: '.png,.jpeg,.jpg,.pdf,.zip' }));
                this.$refs.contactFormRef.setItemValue('materialShow', '');
                throw new Error(this.$t('message.supportFileErrorTip', { type: '.png,.jpeg,.jpg,.pdf,.zip' }));
              }
              this.dataLoading = true;
              try {
                const data = new FormData();
                data.append('file', file);
                const res = await uploadMaterial(data);
                this.dataLoading = false;
                return { fileUrl: res };
              } catch (err) {
                this.dataLoading = false;
                throw err;
              }
            },
          },
        },
      ],
      requesting: false,
      bankList: [],
      euCountryMap: {},
      materialTemplates: {}, // 模板资料字典，用于可下载的模板
      materialTemplatesOriginData: [], // 模板资料列表
      editMaterialList: [], // 被修改的模板资料列表
      materialNameList: [],
      registration: '', // 商户注册地
      vatCountryList: [],
      countryList: [],
      fromTransfer: false,
      isSaveContact: false,
      countryCodeMap: {
        BR: '+55',
        PH: '+63',
        TH: '+66',
        VN: '+84',
      },
    };
  },
  mounted() {
    const { contactId, from } = this.$route.query;
    if (from === 'transfer') {
      this.fromTransfer = true;
    }
    this.contactId = contactId;
    if (this.contactId) {
      this.titleText = this.$t('message.contact.editContact');
    } else {
      this.titleText = this.$t('message.contact.addContact');
    }
    this.initData();
  },
  computed: {
    ...mapGetters([
      'app',
    ]),
  },
  methods: {
    async initData() {
      this.dataLoading = true;
      const accountInfo = await AccountInfo.getAccountInfo();
      this.registration = accountInfo.country;
      this.dictionary = await GlobalData.getDictList(['PAYOUT_CURRENCY', 'BANK_NAME', 'MATERIAL_NAME_URL', 'MATERIAL_NAME']);
      let currencyList = this.dictionary.PAYOUT_CURRENCY;
      this.materialNameList = initSelectedData(this.dictionary.MATERIAL_NAME, 'description', 'value', false);
      const materialNameUrlList = this.dictionary.MATERIAL_NAME_URL;
      if (materialNameUrlList && materialNameUrlList.length) {
        for (let i = 0; i < materialNameUrlList.length; i++) {
          const item = materialNameUrlList[i];
          this.materialTemplates[item.value] = item.description;
        }
      }
      if (`${this.app.userLogin.businessType}` === '0' || this.businessType === 'payout') { // B2B 商户时
        // 当商户为 B2B 商户时，移除 CNY 币种
        currencyList = currencyList.filter(item => item.value !== 'CNY');
        this.$refs.contactFormRef.setItemHide('material', true);
        this.$refs.contactFormRef.setItemHide('bankName1', false);
        this.$refs.contactFormRef.setItemHide('swiftCode', false);
      }
      this.$refs.contactFormRef.setValidationSelectOptions('payoutCurrency', currencyList);
      this.bankList = this.dictionary.BANK_NAME.map(item => ({
        label: item.description,
        value: item.value,
      }));
      // 设置银行名称列表
      this.$refs.contactFormRef.setValidationSelectOptions('bankName', this.filterBank(''));
      // 获取收款方国家、收款行国家
      const res = await getCountryGroupListByName({ countryGroupName: 'payment-ALL' });
      this.countryList = (res.countryGroupInfos || []).map((item) => ({
        description: item.description,
        value: item.value,
      }));
      this.$refs.contactFormRef.setValidationSelectOptions('bankCountry', shallowClones(this.countryList));
      this.$refs.contactFormRef.setValidationSelectOptions('payeeCountry', shallowClones(this.countryList));
      // 获取欧洲国家
      const res1 = await getCountryGroupListByName({ countryGroupName: 'payment-EU' });
      const euCountryList = (res1.countryGroupInfos || []).map((item) => ({
        description: item.description,
        value: item.value,
      }));
      this.euCountryMap = arr2obj(euCountryList, { key: 'value' });

      const vatCountryListRes = await getCountryGroupListByName({ countryGroupName: 'VAT-Groups' });
      this.vatCountryList = (vatCountryListRes.countryGroupInfos || []).map((item) => ({
        description: item.description,
        value: item.value,
      }));

      // 获取联系人详情
      if (this.contactId) {
        const res = await getPayeeInfo({ cardInfoId: this.contactId });
        this.editMaterialList = res.materialList;
        const data = {};
        [
          'accountBelong', 'entityType', 'payoutCurrency', 'bankCountry', 'paymentMethod',
          'payee', 'payeeCountry', 'payeeAddress', 'bankAccount', 'cardNumber', 'sortCode',
          'swiftCode', 'idNumber', 'bankName', 'material', 'materialName', 'bankAddress',
          'phone', 'firstName', 'lastName', 'vatNumber', 'address', 'zipCode', 'companyName',
        ].forEach(k => {
          if (res[k]) {
            data[k] = res[k];
          }
        });

        if (`${this.app.userLogin.businessType}` === '0' || this.businessType === 'payout') {
          data.bankName1 = data.bankName;
        }

        this.$refs.contactFormRef.assignmentFormValue(data);
        this.$refs.contactFormRef.setItemDisabled('cardNumber', true);
        if (data.material) {
          const fileName = data.materialName ? data.materialName : data.material.substring(data.material.lastIndexOf('/') + 1);
          this.$refs.contactFormRef.getValidationItem('material').uploadRange.uploadFileList.push({
            url: data.material,
            name: fileName,
            uploadTime: res.updateTime,
          });
        }

        if (data.accountBelong === '2') {
          this.$refs.contactFormRef.setValidationSelectOptions('payeeCountry', shallowClones(this.vatCountryList));
        }

        if (res.materialList) {
          this.setMaterialList(res.materialList, true);
        }

        this.onChangeValue();
      }
      // if (this.contactId) {
      //   this.getMaterialList(true);
      // }
      this.dataLoading = false;
    },
    filterBank(value) {
      const maxLength = 500;
      let list = this.bankList.slice(0, maxLength);
      if (value) {
        list = this.bankList.filter((item) => item.label.indexOf(value) > -1);
        if (list.length > maxLength) {
          list = list.slice(0, maxLength);
        }
      }
      return list;
    },
    onChangeValue() {
      const form = this.$refs.contactFormRef;
      const data = this.$refs.contactFormRef.getFormJson();
      const {
        payoutCurrency, bankCountry, entityType,
        accountBelong,
      } = data;
      let { paymentMethod } = data;

      // 重置一些属性
      form.setItemHide('idNumber', true);
      form.setItemHide('phone', true);
      form.setItemHide('bankName1', true);
      form.setItemHide('bankName', true);
      form.setItemHide('companyName', true);
      form.setItemHide('firstName', true);
      form.setItemHide('lastName', true);
      form.setItemHide('swiftCode', true);
      form.setItemHide('sortCode', true);
      form.setItemRequire('bankAddress', true);
      form.setItemRequire('payeeAddress', true);
      form.setItemHide('address', true);
      form.setItemHide('zipCode', true);
      form.setItemHide('vatNumber', true);

      if (accountBelong === '2') { // VAT 缴费时
        form.setItemDisabled('entityType', true);
        form.setItemValue('entityType', '0');
        form.setItemHide('material', true);
      } else if (this.app.userLogin.businessType === '0') { // Payout 商户
        form.setItemDisabled('entityType', false);
        form.setItemHide('material', true);
      } else {
        form.setItemDisabled('entityType', false);
        form.setItemHide('material', this.materialTemplatesOriginData.length > 0);
      }

      let paymentMethodOptions = [];
      if (payoutCurrency === 'CNY' && bankCountry === 'CN') { // 出款币种为CNY,收款银行为：中国 只展示 LOCAL
        paymentMethodOptions = [{
          label: 'LOCAL',
          value: '0',
        }];
        paymentMethod = '0';
      } else if ((payoutCurrency === 'GBP' && bankCountry === 'GB') || (payoutCurrency === 'EUR' && !!this.euCountryMap[bankCountry])) {
        // 出款币种为GBP,收款银行为：英国 或出款币种为EUR,收款银行为：欧洲才展示LOCAL支付方式；
        paymentMethodOptions = [{
          label: 'LOCAL',
          value: '0',
        }, {
          label: 'SWIFT',
          value: '1',
        }];
      } else { // 其他只展示 SWIFT
        paymentMethodOptions = [{
          label: 'SWIFT',
          value: '1',
        }];
        paymentMethod = '1';
      }
      form.setItemValue('paymentMethod', paymentMethod);
      form.setValidationSelectOptions('paymentMethod', paymentMethodOptions);

      if (payoutCurrency === 'CNY') { // 当出款币种为 CNY 时，
        if (`${paymentMethod}` === '0') { // 出款币种为 CNY 且支付方式为 LOCAL
          form.setItemHide('idNumber', false); // 显示`身份证号/组织机构代码`且必填
          form.setItemHide('bankName', false); // 显示`银行名称`且必填

          // “银行地址”、“收款方地址”改为“选填”
          form.setItemRequire('bankAddress', false);
          form.setItemRequire('payeeAddress', false);

          if (`${entityType}` === '1') { // 出款币种为 CNY 且支付方式为 LOCAL, 且主体类型为“个人”时 必填
            form.setItemHide('phone', false);
          }
        }
      }

      // 支付币种为EUR,收款银行为：欧洲国家时展示，且必填；
      if (payoutCurrency === 'EUR' && !!this.euCountryMap[bankCountry]) {
        form.setItemName('cardNumber', `${this.$t('message.contact.accountNumber')}/IBAN`);
      } else {
        form.setItemName('cardNumber', this.$t('message.contact.accountNumber'));
      }

      // 支付币种为GBP,收款银行为：英国；支付方式为LOCAL时展示，且必填；
      if (payoutCurrency === 'GBP' && `${paymentMethod}` === '0' && bankCountry === 'GB') {
        form.setItemHide('sortCode', false);
      }

      // 支付方式为SWIFT时展示，必填, 或者是 Payout 商户时展示
      if (`${paymentMethod}` === '1' || this.app.userLogin.businessType === '0') {
        form.setItemHide('swiftCode', false);
      }

      if (`${this.app.userLogin.businessType}` === '0') { // Payout 商户特有字段
        form.setItemHide('bankName1', false);
        // address: 当“收款行国家/地区”为Canada、Egypt、Saudi Arabia、United States时必填
        if (['CA', 'EG', 'SA', 'US'].includes(bankCountry)) {
          form.setItemHide('address', false);
        }
        // phone: 当“收款行国家/地区”为 Brazil、Philippines、Thailand、Vietnam 时必填
        if (['BR', 'PH', 'TH', 'VN'].includes(bankCountry)) {
          form.getValidationItem('phone').prepend = this.countryCodeMap[bankCountry];
          form.setItemHide('phone', false);
        }
        // zipCode: 当“收款行国家/地区”为Canada 或 Egypt时必填
        if (['CA', 'EG'].includes(bankCountry)) {
          form.setItemHide('zipCode', false);
        }
        // vatNumber: 当“收款行国家/地区”为Brazil时必填
        if (['BR'].includes(bankCountry)) {
          form.setItemHide('vatNumber', false);
          if (`${entityType}` === '1') { // 个人
            form.getValidationItem('vatNumber').maxLength = 11;
            form.setItemName('vatNumber', 'CPF');
          } else if (`${entityType}` === '0') {
            form.getValidationItem('vatNumber').maxLength = 14;
            form.setItemName('vatNumber', 'CNPJ');
          }
        }

        if (`${entityType}` === '1') { // 个人
          form.setItemHide('firstName', false);
          form.setItemHide('lastName', false);
        } else if (`${entityType}` === '0') { // 企业
          form.setItemHide('companyName', false);
        }
      }
    },
    onClose() {
      this.$router.go(-1);
    },
    onSubmit() {
      this.$refs.contactFormRef.toFormValid(async (params) => {
        const data = {};
        const requireMaterial = (params.accountBelong !== '2' && this.app.userLogin.businessType !== '0');
        if (requireMaterial && this.materialTemplatesOriginData.length) { // 模板资料列表
          const materialList = [];
          for (let i = 0; i < this.materialTemplatesOriginData.length; i++) {
            const materialTemplatesOriginDataItem = this.materialTemplatesOriginData[i];
            if (materialTemplatesOriginDataItem.materialFormat === '2') {
              materialList.push({
                ...materialTemplatesOriginDataItem,
                fileUrl: this.$refs.contactFormRef.getItemValue(materialTemplatesOriginDataItem.id),
              });
            } else {
              const fileList = this.$refs.contactFormRef.getValidationItem(materialTemplatesOriginDataItem.id).uploadRange.uploadFileList;
              if (!fileList.length && materialTemplatesOriginDataItem.display && materialTemplatesOriginDataItem.required) {
                ShowWarningMessage(this.$t('message.emptyUploadFileTip'));
                return;
              }
              if (fileList.length) {
                materialList.push({
                  ...materialTemplatesOriginDataItem,
                  fileUrl: fileList[0].url,
                });
              } else {
                materialList.push({
                  ...materialTemplatesOriginDataItem,
                  fileUrl: '',
                });
              }
            }
          }
          data.materialList = materialList;
        } else if (requireMaterial) { // 模板资料列表为空
          const fileList = this.$refs.contactFormRef.getValidationItem('material').uploadRange.uploadFileList;
          if (!fileList.length) {
            ShowWarningMessage(this.$t('message.emptyUploadFileTip'));
            return;
          }
          data.material = fileList[0].url;
          data.materialName = fileList[0].name;
        }
        // 共有字段
        const commonKeys = [
          'accountBelong', 'entityType', 'payoutCurrency', 'bankCountry', 'paymentMethod',
          'payee', 'payeeCountry', 'payeeAddress', 'bankAccount', 'cardNumber', 'bankAddress',
        ];
        if (this.contactId) {
          data.cardInfoId = this.contactId;
        }
        commonKeys.forEach(k => {
          data[k] = params[k];
        });

        const { payoutCurrency, paymentMethod, bankCountry, entityType } = params;
        // sortCode: 支付币种为GBP,收款银行为：英国；支付方式为LOCAL时展示，且必填；
        if (payoutCurrency === 'GBP' && `${paymentMethod}` === '0' && bankCountry === 'GB') {
          data.sortCode = params.sortCode;
        }

        // swiftCode: 支付方式为SWIFT 或 Payout 商户时展示，必填
        if (`${paymentMethod}` === '1' || `${this.app.userLogin.businessType}` === '0') {
          data.swiftCode = params.swiftCode;
        }

        if (`${this.app.userLogin.businessType}` === '0') { // Payout 商户特有字段
          data.bankName = params.bankName1;
          if (`${entityType}` === '1') { // 个人
            data.firstName = params.firstName;
            data.lastName = params.lastName;
          } else if (`${entityType}` === '0') { // 企业
            data.companyName = params.companyName;
          }

          // address: 当“收款行国家/地区”为Canada、Egypt、Saudi Arabia、United States时必填
          if (['CA', 'EG', 'SA', 'US'].includes(bankCountry)) {
            data.address = params.address;
          }
          // phone: 当“收款行国家/地区”为 Brazil、Philippines、Thailand、Vietnam 时必填
          if (['BR', 'PH', 'TH', 'VN'].includes(bankCountry)) {
            data.phone = params.phone;
            data.areaCode = this.countryCodeMap[bankCountry];
          }
          // zipCode: 当“收款行国家/地区”为Canada 或 Egypt时必填
          if (['CA', 'EG'].includes(bankCountry)) {
            data.zipCode = params.zipCode;
          }
          // vatNumber: 当“收款行国家/地区”为Brazil时必填
          if (['BR'].includes(bankCountry)) {
            data.vatNumber = params.vatNumber;
          }
        }

        // 出款币种为 CNY 且支付方式为 LOCAL
        if (payoutCurrency === 'CNY' && `${paymentMethod}` === '0') {
          data.idNumber = params.idNumber;
          data.bankName = params.bankName;

          if (`${entityType}` === '1') { // 出款币种为 CNY 且支付方式为 LOCAL, 且主体类型为“个人”时 必填
            data.phone = params.phone;
          }
        }

        if (data.payee) {
          data.payee = data.payee.trim();
        }
        if (data.bankAccount) {
          data.bankAccount = data.bankAccount.trim();
        }
        data.businessType = this.businessType ? (this.businessType === 'payout' ? 'PAYOUT' : 'ACQUIRING') : null;
        let MethodSub = postAddPayeeVerify;
        if (this.contactId) {
          MethodSub = postUpdatePayeeVerify; // 修改
        } else if (this.fromTransfer && !this.isSaveContact) { // 从付款页过来且选择不保存时，
          MethodSub = postValidatePayee;
        }
        this.dataLoading = true;
        try {
          const res = await MethodSub(data);
          this.dataLoading = false;
          if (this.fromTransfer) { // 如果是从付款页跳过来的则跳回付款页
            const transferData = {
              contactInfo: data,
            };
            window.sessionStorage.setItem('transferData', JSON.stringify(transferData));
            this.$router.replace({
              path: '/transfer/transfer/add/amount',
              query: this.isSaveContact ? { id: res } : {},
            });
            return;
          }
          ShowSuccessMessage(this.$t('message.submitSuccessfulTip'));
          this.$router.go(-1);
        } catch (err) {
          //
        }
        this.dataLoading = false;
      });
    },
    bankAddressValidate(rule, value, callback) {
      const data = this.$refs.contactFormRef.getFormJson();
      let required = true;
      const { payoutCurrency, paymentMethod } = data;
      // 出款币种为 CNY 且支付方式为 LOCAL, 选填
      if (payoutCurrency === 'CNY' && `${paymentMethod}` === '0') {
        required = false;
      }
      // 必填验证
      if (required && REQUIRED.regExp.test(value)) {
        callback(new Error(this.$t('message.validator.required')));
        return;
      }
      callback();
    },
    payeeAddressValidate(rule, value, callback) {
      const data = this.$refs.contactFormRef.getFormJson();
      let required = true;
      const { payoutCurrency, paymentMethod } = data;
      // 出款币种为 CNY 且支付方式为 LOCAL, 选填
      if (payoutCurrency === 'CNY' && `${paymentMethod}` === '0') {
        required = false;
      }
      // 必填验证
      if (required && REQUIRED.regExp.test(value)) {
        callback(new Error(this.$t('message.validator.required')));
        return;
      }
      callback();
    },
    validateBankName(rule, value, callback) {
      if (!value || REQUIRED.regExp.test(value)) {
        callback(new Error(this.$t('message.validator.required')));
        return;
      }
      if (value.length < 5 || value.length >= 80) {
        callback(new Error(this.$t('message.validator.inputLength', { length: '[5, 80)' })));
        return;
      }
      callback();
    },
    validateAddress(rule, value, callback) {
      if (!value || REQUIRED.regExp.test(value)) {
        callback(new Error(this.$t('message.validator.required')));
        return;
      }
      if (value.length < 5 || value.length >= 200) {
        callback(new Error(this.$t('message.validator.inputLength', { length: '[5, 200)' })));
        return;
      }
      callback();
    },
    validatePhone(rule, value, callback) {
      const data = this.$refs.contactFormRef.getFormJson();
      const { payoutCurrency, bankCountry } = data;
      if (!value || REQUIRED.regExp.test(value)) {
        callback(new Error(this.$t('message.validator.required')));
        return;
      }
      if (payoutCurrency === 'CNY' && bankCountry === 'CN' && !PHONE_NUMBER.regExp.test(value)) {
        callback(new Error(this.$t('message.validator.inputPhone')));
        return;
      }
      callback();
    },
    validateZipCode(rule, value, callback) {
      if (!value || REQUIRED.regExp.test(value)) {
        callback(new Error(this.$t('message.validator.required')));
        return;
      }
      if (value.length < 4 || value.length >= 8) {
        callback(new Error(this.$t('message.validator.inputLength', { length: '[4, 8)' })));
        return;
      }
      callback();
    },
    validateVatNumber(rule, value, callback) {
      const data = this.$refs.contactFormRef.getFormJson();
      const { entityType } = data;
      if (!value || REQUIRED.regExp.test(value)) {
        callback(new Error(this.$t('message.validator.required')));
        return;
      }
      if (`${entityType}` === '1' && value.length !== 11) {
        callback(new Error(this.$t('message.validator.inputLength', { length: '11' })));
        return;
      }
      if (`${entityType}` === '0' && value.length !== 14) {
        callback(new Error(this.$t('message.validator.inputLength', { length: '14' })));
        return;
      }
      callback();
    },
    setMaterialList(list = [], init = false) {
      const form = this.$refs.contactFormRef;
      this.materialTemplatesOriginData = list;
      if (list.length) {
        for (let i = 0; i < list.length; i++) {
          let resItem = list[i];
          if (init && this.editMaterialList && this.editMaterialList.length) { // 修改页面模板资料数据初始化
            for (let j = 0; j < this.editMaterialList.length; j++) {
              if (this.editMaterialList[j].materialName === resItem.materialName && this.editMaterialList[j].templateId === resItem.templateId) {
                resItem = { ...resItem, ...this.editMaterialList[j] };
              }
            }
          }
          const conditionItem = {
            key: resItem.id,
            name: getEnantiomericValue(this.materialNameList, resItem.materialName) || resItem.materialName,
            type: resItem.materialFormat === '2' ? 'input' : 'upload_file', // 0:附件，1:图片，2:文本
            rule: resItem.required ? [REQUIRED] : [],
            defValue: resItem.materialFormat === '2' ? (resItem.fileUrl ? resItem.fileUrl : '') : (resItem.fileUrl ? [resItem.fileUrl] : []),
            isShowStar: resItem.required,
            isHide: !resItem.display, // 根据配置是否显示
          };
          if (resItem.materialFormat !== '2') {
            const fileRule = resItem.materialFormat === '0' ? 'application/zip,application/x-zip-compressed,.zip,application/pdf,image/png,image/jpeg,image/jpg' : 'image/png,image/jpeg,image/jpg';
            const acceptFileTypeExt = resItem.materialFormat === '0' ? '.png,.jpeg,.jpg,.pdf,.zip' : '.png,.jpeg,.jpg';
            const acceptFileType = resItem.materialFormat === '0' ? 'png,jpeg,jpg,pdf,zip' : 'png,jpeg,jpg';
            conditionItem.uploadRange = {
              fileSize: 50,
              uploadFileList: conditionItem.defValue.length ? [{
                url: conditionItem.defValue[0],
                name: `${conditionItem.defValue[0].split('/')[conditionItem.defValue[0].split('/').length - 1]}`,
                updateTime: conditionItem.uploadTime,
              }] : [],
              uploadTips: this.$t('message.upload.supportFileTip', { type: acceptFileType }),
              fileTypeRule: fileRule,
              fileTypeRuleTips: this.$t('message.supportFileErrorTip', { type: acceptFileTypeExt }),
              fileUploadFunc: uploadMaterialUseTemplate,
              status: () => {
                if (resItem.auditStatus === 'pass' && resItem.fileUrl) { // 上传资料 存在已审核
                  return `<span class="status">${this.$t('message.status.existAuditSuccess')}</span>`;
                } else if (resItem.auditStatus === 'waiting' && resItem.fileUrl) { // 上传资料 存在未审核
                  return `<span class="status">${this.$t('message.status.existUnAudit')}</span>`;
                } else if (resItem.auditStatus === 'fail' && resItem.fileUrl) { // 审核不通过
                  return `<span class="status fail">${this.$t('message.status.auditFail')}</span>`;
                }
                return '';
              },
            };
            if (this.materialTemplates[resItem.materialName]) {
              conditionItem.uploadRange.templateFileName = this.materialTemplates[resItem.materialName];
              const templateFileArr = this.materialTemplates[resItem.materialName].split('/');
              conditionItem.uploadRange.templateFileNameShow = `${templateFileArr[templateFileArr.length - 1]}`;
            }
          }
          form.addItem(conditionItem);
        }
        form.setItemHide('material', true);
      } else {
        form.setItemHide('material', false);
      }
    },
    getMaterialList(init = false) {
      const form = this.$refs.contactFormRef;
      const data = form.getFormJson();
      // B2B 商户不获取材料模板 或 VAT 时不获取材料模板
      if (this.app.userLogin.businessType === '0' || data.accountBelong === '2') {
        // 删除原有模板资料列表
        if (this.materialTemplatesOriginData.length) {
          for (let i = 0; i < this.materialTemplatesOriginData.length; i++) {
            form.removeItem(this.materialTemplatesOriginData[i].id);
          }
        }
        this.materialTemplatesOriginData = [];
        return;
      }
      if (data.accountBelong && data.entityType && data.payoutCurrency) {
        if (!init) {
          this.dataLoading = true;
        }
        // 删除原有模板资料列表
        if (this.materialTemplatesOriginData.length) {
          for (let i = 0; i < this.materialTemplatesOriginData.length; i++) {
            form.removeItem(this.materialTemplatesOriginData[i].id);
          }
        }
        const params = {
          businessType: this.app.userLogin.businessType, // 业务类型, 0:B2B, 1:其它
          payoutTxnType: data.accountBelong === '0' ? '3' : '4', // 出款交易类型,3: 绑卡-自有，4: 绑卡-他人
          entityType: data.entityType, // 主体类型，0:企业；1:个人
          payoutCurrency: data.payoutCurrency, // 出款币种
          registration: this.registration,
        };
        getMaterialInfoList(params).then(res => {
          this.setMaterialList(res, init);
          this.dataLoading = false;
        }).catch(() => {
          this.dataLoading = false;
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/variables.scss';
.contact-form {
  width: 450px;
  margin: 20px auto 0;

  ::v-deep .rhch-upload-container {
    width: 450px;
    min-width: auto;
    .el-upload-dragger, .rhch-file-list {
        width: 450px;
        min-width: auto;
    }

    .rhch-file-list .rhch-file-item .rhcn-filename {
      max-width: 240px;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }

  .btn-save-contact {
    font-size: 14px;
    font-weight: bold;
    color: $color-text-primary;
    line-height: 32px;
  }

  .bottom-box {
    display: flex;
    flex-direction: column;
    align-items: center;

    .btn-previous {
      cursor: pointer;
      color: $color-text-default-button;
      font-weight: 600;
    }
  }
}
</style>
